// ── CrewAdminPage ✦ — Trust & Safety Operations Centre ──
//
// Single ops surface across the 7 moderation collections the
// platform now generates. Constitutional UX: this is a TRUST
// tool, not a punitive dashboard. Calm, professional, fintech-
// ops energy — softer neon accents, red reserved for genuine
// emergencies. Every action persists a reviewer note + an audit
// trail for the safeguarding lead.
//
// Layout: tabbed switcher (Overview · Safeguarding · Video ·
// Messaging · Directed Invites · Push · Digest), each panel is
// a self-contained section that fetches its own slice. Escalate-
// to-DSL ✦ available from inside the relevant report.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, ShieldCheck, FilmStrip, ChatCircleDots, Heart,
  Bell, Envelope, Warning, CheckCircle, CaretRight, Clock,
} from '@phosphor-icons/react';

import { MetricsTile } from './admin/components/MetricsTile';
import { ModerationOverview } from './admin/components/ModerationOverview';
import { ParentDashboardStrip } from './admin/components/ParentDashboardStrip';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Aesthetic — softer accents on purpose. Red is reserved.
const BG = '#06061A';
const PANEL = 'rgba(255,255,255,0.04)';
const PANEL_BORDER = 'rgba(255,255,255,0.08)';
const TEXT = '#F5F5FA';
const TEXT_MUTED = '#9A9ABF';
const BLUE = '#33C3FF';
const PURPLE = '#6C3BFF';
const PINK = '#FF3CAC';
const GOLD = '#FFD166';
const DANGER = '#FF5C8A';
const FONT_STACK = "'Space Grotesk', 'Inter', system-ui, sans-serif";

const STATUS_COLOURS = {
  open:         GOLD,
  reviewing:    BLUE,
  escalated:    DANGER,
  resolved:     '#5EE3A0',
  action_taken: PURPLE,
  suspended:    DANGER,
  pending:      GOLD,
  accepted:     '#5EE3A0',
  declined:     TEXT_MUTED,
  maybe:        BLUE,
  expired:      TEXT_MUTED,
};

const StatusPill = ({ status }) => {
  const s = (status || 'open').toLowerCase();
  const c = STATUS_COLOURS[s] || TEXT_MUTED;
  return (
    <span
      data-testid={`admin-status-${s}`}
      style={{
        padding: '3px 9px', borderRadius: 999, fontSize: 10.5, fontWeight: 800,
        letterSpacing: '0.12em', textTransform: 'uppercase',
        color: c, border: `1px solid ${c}44`,
        background: `${c}14`,
      }}
    >
      {s.replace(/_/g, ' ')}
    </span>
  );
};

const OverviewCard = MetricsTile;

const ActionRow = ({ onAction, onEscalate, busy, statusOptions = ['reviewing', 'resolved', 'action_taken', 'suspended'] }) => {
  const [note, setNote] = useState('');
  const [showEscalate, setShowEscalate] = useState(false);
  return (
    <div style={{ marginTop: 10 }}>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Reviewer note (optional, kept on the audit trail)"
        rows={2}
        maxLength={600}
        style={{
          width: '100%', padding: '8px 10px', borderRadius: 10, color: TEXT,
          background: 'rgba(255,255,255,0.04)', border: `1px solid ${PANEL_BORDER}`,
          fontFamily: FONT_STACK, fontSize: 12.5, outline: 'none', resize: 'vertical',
          lineHeight: 1.5,
        }}
      />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
        {statusOptions.map((s) => {
          const c = STATUS_COLOURS[s] || PURPLE;
          return (
            <button
              key={s}
              onClick={() => onAction(s, note)}
              disabled={busy}
              data-testid={`admin-action-${s}`}
              style={{
                padding: '7px 12px', borderRadius: 999, cursor: busy ? 'wait' : 'pointer',
                background: `${c}1F`, border: `1px solid ${c}55`, color: TEXT,
                fontFamily: FONT_STACK, fontSize: 11.5, fontWeight: 800,
              }}
            >
              {s.replace(/_/g, ' ')}
            </button>
          );
        })}
        {onEscalate && (
          <button
            onClick={() => setShowEscalate((v) => !v)}
            disabled={busy}
            data-testid="admin-escalate-toggle"
            style={{
              padding: '7px 12px', borderRadius: 999, cursor: 'pointer',
              background: `${DANGER}1F`, border: `1px solid ${DANGER}AA`, color: TEXT,
              fontFamily: FONT_STACK, fontSize: 11.5, fontWeight: 800,
              display: 'inline-flex', alignItems: 'center', gap: 5,
            }}
          >
            <Warning size={11} weight="fill" color={DANGER} />
            Escalate to DSL ✦
          </button>
        )}
      </div>
      {showEscalate && onEscalate && (
        <div style={{
          marginTop: 8, padding: 10, borderRadius: 10,
          background: `${DANGER}10`, border: `1px solid ${DANGER}55`,
        }}>
          <p style={{ margin: 0, fontSize: 11.5, color: TEXT_MUTED, lineHeight: 1.5 }}>
            This routes the case to the Designated Safeguarding Lead and logs it on the
            DSL queue. Use for anything involving under-18 risk or supplier misconduct.
          </p>
          <button
            onClick={() => onEscalate(note)}
            disabled={busy}
            data-testid="admin-escalate-confirm"
            style={{
              marginTop: 8, padding: '8px 16px', borderRadius: 999, cursor: 'pointer',
              background: DANGER, border: 'none', color: '#1A0510',
              fontFamily: FONT_STACK, fontSize: 12, fontWeight: 900,
            }}
          >
            Confirm escalation
          </button>
        </div>
      )}
    </div>
  );
};

const SectionShell = ({ children, eyebrow, title, testid }) => (
  <section
    data-testid={testid}
    style={{
      padding: 20, borderRadius: 20, marginBottom: 14,
      background: PANEL, border: `1px solid ${PANEL_BORDER}`,
    }}
  >
    {eyebrow && (
      <p style={{
        margin: 0, fontWeight: 800, fontSize: 10.5, letterSpacing: '0.3em',
        textTransform: 'uppercase', color: BLUE,
      }}>{eyebrow}</p>
    )}
    {title && (
      <p style={{
        margin: '4px 0 12px', fontFamily: FONT_STACK, fontWeight: 900, fontSize: 20,
        color: TEXT, letterSpacing: '-0.01em',
      }}>{title}</p>
    )}
    {children}
  </section>
);

// ── Panels ──────────────────────────────────────────────────
// OverviewPanel + ParentDashboardStrip live in
// /app/frontend/src/pages/crew/admin/components/* (extracted
// 2026-05-20 for maintainability). The local alias keeps the
// existing callsite in the tab renderer minimal.
const OverviewPanel = ModerationOverview;

const SafeguardingPanel = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/crew/admin/safeguarding-reports`);
      setRows(r.data?.reports || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (id, status, note) => {
    setBusy(id);
    try {
      await axios.post(`${API}/crew/admin/safeguarding-reports/${id}/action`, { status, note });
      await load();
    } finally { setBusy(''); }
  };

  const escalate = async (id, reason) => {
    setBusy(id);
    try {
      await axios.post(`${API}/crew/admin/escalate`, { kind: 'safeguarding', report_id: id, reason });
      await load();
    } finally { setBusy(''); }
  };

  return (
    <SectionShell eyebrow="✦ Safeguarding" title="Safeguarding reports" testid="admin-safeguarding">
      {loading && <p style={{ color: TEXT_MUTED, fontSize: 13 }}>Loading…</p>}
      {!loading && rows.length === 0 && (
        <p data-testid="admin-safeguarding-empty" style={{ color: TEXT_MUTED, fontSize: 13.5 }}>
          ✦ All clear — no open safeguarding reports.
        </p>
      )}
      {rows.map((r) => (
        <div
          key={r.id}
          data-testid={`admin-report-${r.id}`}
          style={{
            padding: 14, borderRadius: 14, marginTop: 10,
            background: 'rgba(255,255,255,0.02)', border: `1px solid ${PANEL_BORDER}`,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <StatusPill status={r.status || 'open'} />
              <span style={{ fontSize: 11.5, color: TEXT_MUTED, fontWeight: 700 }}>
                {(r.category || 'safeguarding').replace(/_/g, ' ')}
              </span>
              <span style={{ fontSize: 11, color: TEXT_MUTED }}>
                <Clock size={10} weight="fill" /> {(r.created_at || '').slice(0, 16).replace('T', ' ')}
              </span>
            </div>
          </div>
          <p style={{ margin: '8px 0 6px', color: TEXT, fontSize: 13.5, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
            {r.message}
          </p>
          <p style={{ margin: 0, fontSize: 11, color: TEXT_MUTED, fontStyle: 'italic' }}>
            {r.reporter_email ? `Reporter: ${r.reporter_email}` : 'Anonymous'}
            {r.target_user_id ? ` · target user: ${r.target_user_id}` : ''}
            {r.target_thread_id ? ` · thread: ${r.target_thread_id}` : ''}
          </p>
          {r.review_note && (
            <p style={{ margin: '8px 0 0', fontSize: 12, color: TEXT_MUTED, padding: '6px 10px', borderRadius: 10, background: 'rgba(255,255,255,0.03)' }}>
              <b style={{ color: TEXT }}>Reviewer note ·</b> {r.review_note}
              {r.reviewer_email && <i style={{ color: TEXT_MUTED }}> — {r.reviewer_email}</i>}
            </p>
          )}
          <ActionRow
            busy={busy === r.id}
            onAction={(s, n) => act(r.id, s, n)}
            onEscalate={(n) => escalate(r.id, n)}
          />
        </div>
      ))}
    </SectionShell>
  );
};

const VideoPanel = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/crew/admin/video-reports`, { params: { resolved: false } });
      setRows(r.data?.reports || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (id, status, note, hide_video = null) => {
    setBusy(id);
    try {
      await axios.post(`${API}/crew/admin/video-reports/${id}/action`, { status, note, hide_video });
      await load();
    } finally { setBusy(''); }
  };

  return (
    <SectionShell eyebrow="✦ Video moderation" title="Video reports" testid="admin-video">
      {loading && <p style={{ color: TEXT_MUTED, fontSize: 13 }}>Loading…</p>}
      {!loading && rows.length === 0 && (
        <p data-testid="admin-video-empty" style={{ color: TEXT_MUTED, fontSize: 13.5 }}>
          ✦ Queue clear — no open video reports.
        </p>
      )}
      {rows.map((r) => {
        const t = r.target || {};
        const videoUrl = t.video_url || t.intro_video_url;
        return (
          <div
            key={r.id}
            data-testid={`admin-video-report-${r.id}`}
            style={{
              padding: 14, borderRadius: 14, marginTop: 10,
              background: 'rgba(255,255,255,0.02)', border: `1px solid ${PANEL_BORDER}`,
              display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start',
            }}
          >
            <div style={{ flex: '0 0 200px' }}>
              {videoUrl ? (
                <video
                  src={videoUrl}
                  poster={t.video_poster_url}
                  controls
                  style={{
                    width: '100%', height: 'auto', maxHeight: 200, borderRadius: 10,
                    background: '#000',
                  }}
                />
              ) : (
                <div style={{ padding: 20, color: TEXT_MUTED, fontSize: 12, textAlign: 'center', background: 'rgba(255,255,255,0.03)', borderRadius: 10 }}>
                  No preview
                </div>
              )}
            </div>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <StatusPill status={r.status || (r.resolved ? 'resolved' : 'open')} />
                <span style={{ fontSize: 11.5, color: TEXT_MUTED, fontWeight: 700 }}>
                  {r.target_kind} · {t.title || t.name || r.target_id}
                </span>
              </div>
              <p style={{ margin: '8px 0 6px', color: TEXT, fontSize: 13.5, lineHeight: 1.5 }}>
                "{r.reason || 'No reason provided.'}"
              </p>
              <p style={{ margin: 0, fontSize: 11, color: TEXT_MUTED, fontStyle: 'italic' }}>
                Reporter: {r.reporter_user_id} · {(r.created_at || '').slice(0, 16).replace('T', ' ')}
              </p>
              <ActionRow
                busy={busy === r.id}
                onAction={(s, n) => act(r.id, s, n)}
              />
              <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {t.video_hidden ? (
                  <button
                    onClick={() => act(r.id, 'resolved', 'Restored after review', false)}
                    disabled={busy === r.id}
                    data-testid={`admin-video-restore-${r.id}`}
                    style={{
                      padding: '7px 12px', borderRadius: 999, cursor: 'pointer',
                      background: `${BLUE}22`, border: `1px solid ${BLUE}66`, color: TEXT,
                      fontFamily: FONT_STACK, fontSize: 11.5, fontWeight: 800,
                    }}
                  >
                    Restore video
                  </button>
                ) : (
                  <button
                    onClick={() => act(r.id, 'action_taken', 'Hidden by moderator', true)}
                    disabled={busy === r.id}
                    data-testid={`admin-video-hide-${r.id}`}
                    style={{
                      padding: '7px 12px', borderRadius: 999, cursor: 'pointer',
                      background: `${DANGER}22`, border: `1px solid ${DANGER}66`, color: TEXT,
                      fontFamily: FONT_STACK, fontSize: 11.5, fontWeight: 800,
                    }}
                  >
                    Hide video
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </SectionShell>
  );
};

const MessagingPanel = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/crew/admin/messaging-reports`);
      setRows(r.data?.reports || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (id, status, note) => {
    setBusy(id);
    try {
      await axios.post(`${API}/crew/admin/messaging-reports/${id}/action`, { status, note });
      await load();
    } finally { setBusy(''); }
  };

  return (
    <SectionShell eyebrow="✦ Messaging" title="Messaging reports" testid="admin-messaging">
      {loading && <p style={{ color: TEXT_MUTED, fontSize: 13 }}>Loading…</p>}
      {!loading && rows.length === 0 && (
        <p data-testid="admin-messaging-empty" style={{ color: TEXT_MUTED, fontSize: 13.5 }}>
          ✦ No flagged threads in the last 30 days.
        </p>
      )}
      {rows.map((r) => (
        <div
          key={r.id}
          data-testid={`admin-messaging-report-${r.id}`}
          style={{
            padding: 14, borderRadius: 14, marginTop: 10,
            background: 'rgba(255,255,255,0.02)', border: `1px solid ${PANEL_BORDER}`,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <StatusPill status={r.status || 'open'} />
            <span style={{ fontSize: 11.5, color: TEXT_MUTED }}>
              Thread {r.thread_id} · reporter {r.user_id}
            </span>
          </div>
          <p style={{ margin: '8px 0 6px', color: TEXT, fontSize: 13.5 }}>
            "{r.reason || 'No reason provided.'}"
          </p>
          {(r.recent_messages || []).map((m) => (
            <p
              key={m.id}
              style={{
                margin: '4px 0 0', fontSize: 12, color: TEXT_MUTED, lineHeight: 1.5,
                padding: '6px 10px', borderRadius: 10, background: 'rgba(255,255,255,0.03)',
              }}
            >
              <b style={{ color: TEXT }}>{m.sender_user_id?.slice(0, 12) || 'user'}:</b> {(m.body || '').slice(0, 200)}
            </p>
          ))}
          <ActionRow
            busy={busy === r.id}
            onAction={(s, n) => act(r.id, s, n)}
          />
        </div>
      ))}
    </SectionShell>
  );
};

const DirectedInvitesPanel = () => {
  const [data, setData] = useState({ invites: [], top_senders: [] });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/crew/admin/directed-invites`);
        setData(r.data || {});
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <SectionShell eyebrow="✦ Directed invites" title="Repeat-requested monitoring · 7d" testid="admin-directed">
      {loading && <p style={{ color: TEXT_MUTED, fontSize: 13 }}>Loading…</p>}
      {!loading && (
        <>
          <div data-testid="admin-directed-top-senders" style={{ marginBottom: 14 }}>
            <p style={{ margin: '0 0 6px', fontSize: 11, color: TEXT_MUTED, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase' }}>
              Top senders
            </p>
            <div style={{ display: 'grid', gap: 6, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
              {(data.top_senders || []).map((s) => (
                <div
                  key={s.supplier_user_id}
                  data-testid={`admin-directed-supplier-${s.supplier_user_id}`}
                  style={{
                    padding: 10, borderRadius: 12, background: 'rgba(255,255,255,0.03)',
                    border: `1px solid ${PANEL_BORDER}`,
                  }}
                >
                  <p style={{ margin: 0, fontWeight: 800, fontSize: 13, color: TEXT }}>
                    {s.supplier_name || s.supplier_user_id}
                  </p>
                  <p style={{ margin: '2px 0 0', fontSize: 11.5, color: TEXT_MUTED }}>
                    {s.total} sent · accepted {s.accepted || 0} · declined {s.declined || 0} · maybe {s.maybe || 0}
                  </p>
                </div>
              ))}
              {(data.top_senders || []).length === 0 && (
                <p style={{ color: TEXT_MUTED, fontSize: 12.5 }}>No directed invites in the last 7 days.</p>
              )}
            </div>
          </div>
        </>
      )}
    </SectionShell>
  );
};

const PushPanel = () => {
  const [data, setData] = useState({ sends: [], channels: {}, failures: 0 });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/crew/admin/push-log`);
        setData(r.data || {});
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <SectionShell eyebrow="✦ Push log" title="Notifications · last 7 days" testid="admin-push">
      {loading && <p style={{ color: TEXT_MUTED, fontSize: 13 }}>Loading…</p>}
      {!loading && (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            <span style={{ padding: '4px 10px', borderRadius: 999, fontSize: 11.5, fontWeight: 800, color: TEXT, background: `${PURPLE}22`, border: `1px solid ${PURPLE}55` }}>
              {data.count || 0} sends
            </span>
            <span style={{ padding: '4px 10px', borderRadius: 999, fontSize: 11.5, fontWeight: 800, color: TEXT, background: `${DANGER}22`, border: `1px solid ${DANGER}55` }}>
              {data.failures || 0} failed
            </span>
            {Object.entries(data.channels || {}).map(([ch, n]) => (
              <span key={ch} style={{ padding: '4px 10px', borderRadius: 999, fontSize: 11.5, fontWeight: 700, color: TEXT_MUTED, background: 'rgba(255,255,255,0.04)', border: `1px solid ${PANEL_BORDER}` }}>
                {ch}: {n}
              </span>
            ))}
          </div>
          <div style={{ maxHeight: 360, overflow: 'auto' }}>
            {(data.sends || []).slice(0, 50).map((s) => (
              <div
                key={s.id || `${s.user_id}-${s.created_at}`}
                style={{
                  padding: '8px 10px', borderRadius: 10, marginTop: 6,
                  background: 'rgba(255,255,255,0.02)', border: `1px solid ${PANEL_BORDER}`,
                  display: 'flex', gap: 8, alignItems: 'flex-start',
                }}
              >
                {s.ok === false
                  ? <Warning size={12} weight="fill" color={DANGER} style={{ marginTop: 3, flexShrink: 0 }} />
                  : <CheckCircle size={12} weight="fill" color="#5EE3A0" style={{ marginTop: 3, flexShrink: 0 }} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: 12.5, fontWeight: 700, color: TEXT }}>
                    {s.title || '(no title)'}
                  </p>
                  <p style={{ margin: '2px 0 0', fontSize: 11.5, color: TEXT_MUTED }}>
                    {s.channel || '—'} · {(s.created_at || '').slice(0, 16).replace('T', ' ')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </SectionShell>
  );
};

const DigestPanel = () => {
  const [data, setData] = useState({ sends: [], delivered: 0, failures: 0 });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/crew/admin/digest-log`);
        setData(r.data || {});
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <SectionShell eyebrow="✦ Digest log" title="Crew Wins ✦ · last 7 days" testid="admin-digest">
      {loading && <p style={{ color: TEXT_MUTED, fontSize: 13 }}>Loading…</p>}
      {!loading && (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            <span style={{ padding: '4px 10px', borderRadius: 999, fontSize: 11.5, fontWeight: 800, color: TEXT, background: `${BLUE}22`, border: `1px solid ${BLUE}55` }}>
              {data.count || 0} digests
            </span>
            <span style={{ padding: '4px 10px', borderRadius: 999, fontSize: 11.5, fontWeight: 800, color: TEXT, background: `#5EE3A022`, border: `1px solid #5EE3A055` }}>
              {data.delivered || 0} delivered
            </span>
            {data.failures > 0 && (
              <span style={{ padding: '4px 10px', borderRadius: 999, fontSize: 11.5, fontWeight: 800, color: TEXT, background: `${DANGER}22`, border: `1px solid ${DANGER}55` }}>
                {data.failures} failed
              </span>
            )}
          </div>
          <div style={{ maxHeight: 360, overflow: 'auto' }}>
            {(data.sends || []).slice(0, 50).map((d) => (
              <div
                key={`${d.user_id}-${d.sent_at}`}
                style={{
                  padding: '8px 10px', borderRadius: 10, marginTop: 6,
                  background: 'rgba(255,255,255,0.02)', border: `1px solid ${PANEL_BORDER}`,
                }}
              >
                <p style={{ margin: 0, fontSize: 12.5, color: TEXT }}>
                  {d.email || d.user_id}
                </p>
                <p style={{ margin: '2px 0 0', fontSize: 11.5, color: TEXT_MUTED }}>
                  {(d.sent_at || '').slice(0, 16).replace('T', ' ')} · shares {d.shares || 0} · unlocks {d.unlocks || 0}
                </p>
              </div>
            ))}
            {(data.sends || []).length === 0 && (
              <p style={{ color: TEXT_MUTED, fontSize: 12.5 }}>No digest sends in this window.</p>
            )}
          </div>
        </>
      )}
    </SectionShell>
  );
};

// ── Main page ──────────────────────────────────────────────

const TABS = [
  { id: 'overview',     label: 'Overview',         icon: ShieldCheck },
  { id: 'safeguarding', label: 'Safeguarding',     icon: ShieldCheck },
  { id: 'video',        label: 'Video',            icon: FilmStrip },
  { id: 'messaging',    label: 'Messaging',        icon: ChatCircleDots },
  { id: 'directed',     label: 'Directed invites', icon: Heart },
  { id: 'push',         label: 'Push',             icon: Bell },
  { id: 'digest',       label: 'Digest',           icon: Envelope },
];

const CrewAdminPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [overview, setOverview] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true); setError('');
      try {
        const r = await axios.get(`${API}/crew/admin/overview`);
        setOverview(r.data || {});
      } catch (err) {
        if (err?.response?.status === 401) navigate('/login?next=/crew/admin');
        if (err?.response?.status === 403) setError('Admin only — your account is not a moderator.');
        else setError(err?.response?.data?.detail || 'Could not load admin overview');
      } finally { setLoading(false); }
    })();
  }, [navigate]);

  return (
    <div
      data-themed="crew"
      data-testid="crew-admin-page"
      style={{
        background: BG, minHeight: '100vh', color: TEXT,
        fontFamily: FONT_STACK, paddingBottom: 60,
      }}
    >
      <header style={{
        padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 12,
        borderBottom: `1px solid ${PANEL_BORDER}`, position: 'sticky', top: 0,
        background: `${BG}EE`, backdropFilter: 'blur(12px)', zIndex: 10,
      }}>
        <button
          onClick={() => navigate('/crew')}
          data-testid="admin-back"
          style={{
            background: 'transparent', border: 'none', color: TEXT, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px',
            borderRadius: 999, fontSize: 13, fontWeight: 700,
          }}
        >
          <ArrowLeft size={14} weight="bold" /> Crew
        </button>
        <div>
          <p style={{ margin: 0, fontSize: 10.5, fontWeight: 800, letterSpacing: '0.32em', textTransform: 'uppercase', color: BLUE }}>
            ✦ Trust & Safety
          </p>
          <p style={{ margin: '2px 0 0', fontFamily: FONT_STACK, fontWeight: 900, fontSize: 22, color: TEXT, letterSpacing: '-0.01em' }}>
            Moderation Centre
          </p>
        </div>
      </header>

      <nav
        data-testid="admin-tabs"
        style={{
          padding: '12px 24px 0', display: 'flex', gap: 6, flexWrap: 'wrap',
          borderBottom: `1px solid ${PANEL_BORDER}`,
        }}
      >
        {TABS.map((t) => {
          const active = t.id === tab;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              data-active={active ? '1' : '0'}
              style={{
                padding: '8px 14px', borderRadius: '14px 14px 0 0', cursor: 'pointer',
                background: active
                  ? `linear-gradient(180deg, ${PANEL}, transparent)`
                  : 'transparent',
                border: active ? `1px solid ${PANEL_BORDER}` : '1px solid transparent',
                borderBottom: active ? `1px solid ${BG}` : 'none',
                color: active ? TEXT : TEXT_MUTED,
                fontFamily: FONT_STACK, fontSize: 12.5, fontWeight: 800,
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
            >
              <t.icon size={12} weight="fill" color={active ? BLUE : TEXT_MUTED} />
              {t.label}
            </button>
          );
        })}
      </nav>

      <main style={{ padding: 24, maxWidth: 920, margin: '0 auto' }}>
        {error && (
          <div
            data-testid="admin-error"
            style={{ padding: 14, borderRadius: 14, background: `${DANGER}10`, border: `1px solid ${DANGER}66`, color: TEXT, fontSize: 13 }}
          >
            {error}
          </div>
        )}
        {!error && loading && <p style={{ color: TEXT_MUTED, fontSize: 13 }}>Loading…</p>}
        {!error && !loading && tab === 'overview'     && (
          <>
            <OverviewPanel overview={overview} onJump={setTab} />
            <ParentDashboardStrip onJump={setTab} />
          </>
        )}
        {!error && !loading && tab === 'safeguarding' && <SafeguardingPanel />}
        {!error && !loading && tab === 'video'        && <VideoPanel />}
        {!error && !loading && tab === 'messaging'    && <MessagingPanel />}
        {!error && !loading && tab === 'directed'     && <DirectedInvitesPanel />}
        {!error && !loading && tab === 'push'         && <PushPanel />}
        {!error && !loading && tab === 'digest'       && <DigestPanel />}

        {/* Constitutional Contract Map ✦ — quiet entry point. */}
        <p
          data-testid="crew-admin-contracts-link-row"
          style={{
            margin: '32px 0 0', textAlign: 'center',
            color: TEXT_MUTED, fontSize: 11.5, lineHeight: 1.7,
          }}
        >
          <a
            href="/internal/contracts"
            data-testid="crew-admin-contracts-link"
            style={{ color: TEXT_MUTED, textDecoration: 'none', borderBottom: '1px dotted ' + TEXT_MUTED }}
          >
            View Constitutional Contract Map ✦
          </a>
          <span style={{ opacity: 0.6 }}> · internal · admin-only</span>
        </p>
      </main>
    </div>
  );
};

export default CrewAdminPage;
